/**
 * #583 — return a guest to the "Friends with Benefits" section after sign-in.
 *
 * Guests can't refer (Omneo Invites creates the invite AS the referrer's
 * profile), so the section renders a sign-in CTA instead of the form. Signing in
 * reloads the page, which drops the visitor back at the top — well above the
 * section they were using, with the now-usable form out of sight.
 *
 * So: the CTA click records the section id in sessionStorage, and the next load
 * of a page carrying that section scrolls to it and clears the flag, so it
 * happens once and never repeats. Both halves live here because the section
 * renders the CTA only to guests and the form only to signed-in customers — a
 * script in either branch alone would miss one side of the round trip.
 *
 * sessionStorage (not localStorage) so the intent dies with the tab, plus a
 * freshness window for the case where the visitor dismisses the sign-in sheet
 * and comes back to the page much later in the same tab.
 */
(function () {
  'use strict';

  const KEY = 'jag_referral_scroll';
  /** Beyond this the flag is stale — the visitor is no longer mid sign-in. */
  const MAX_AGE_MS = 30 * 60 * 1000;

  const block = document.querySelector('[data-jag-referral-sign-in], [data-jag-referral-form]');
  if (!block) return;

  const section = block.closest('[id^="shopify-section-"]') || block;
  if (!section.id) return;

  const read = () => {
    try {
      return sessionStorage.getItem(KEY);
    } catch {
      return null;
    }
  };
  const clear = () => {
    try {
      sessionStorage.removeItem(KEY);
    } catch {
      /* private mode — nothing to clear */
    }
  };

  // ------------------------------------------------------------- remember
  // jag-account.js intercepts this click to open the <shopify-account> sheet,
  // so the flag has to be written on the click itself, not on a navigation.
  const cta = document.querySelector('[data-jag-referral-sign-in]');
  if (cta) {
    cta.addEventListener('click', () => {
      try {
        sessionStorage.setItem(KEY, `${section.id}:${Date.now()}`);
      } catch {
        /* private mode — the visitor just lands at the top, as before */
      }
    });
  }

  // -------------------------------------------------------------- restore
  const flag = read();
  if (!flag) return;

  const separator = flag.lastIndexOf(':');
  const storedId = separator === -1 ? flag : flag.slice(0, separator);
  const storedAt = separator === -1 ? 0 : Number(flag.slice(separator + 1));

  // A flag for a different section belongs to another page — leave it alone.
  if (storedId !== section.id) return;

  clear();
  if (!Number.isFinite(storedAt) || Date.now() - storedAt > MAX_AGE_MS) return;

  const scroll = () => {
    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // `html` carries scroll-padding-top for the sticky header (theme.css), which
    // scrollIntoView honours — so 'start' clears the header without our own maths.
    section.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  };

  // Wait for images to settle, or the section moves out from under the scroll.
  if (document.readyState === 'complete') {
    requestAnimationFrame(scroll);
  } else {
    window.addEventListener('load', () => requestAnimationFrame(scroll), { once: true });
  }
})();
